import { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (v) => (v == null ? 'unknown' : `${Number(v) < 0 ? '−' : ''}$${Math.abs(Number(v)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtAt = (s) => (s ? new Date(s).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

// The records behind one summary figure; their total is the figure shown on the card.
export const FinanceRecordsDialog = ({ metric, title, range, onClose }) => {
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!metric) return;
        setData(null);
        axios.get(`${API}/admin/finance/records`, { params: { metric, from_day: range.from, to_day: range.to } })
            .then((r) => setData(r.data)).catch(() => { toast.error('Could not load the records'); onClose(); });
    }, [metric, range.from, range.to, onClose]);

    const exportCsv = async () => {
        const r = await axios.get(`${API}/admin/finance/records`, { params: { metric, from_day: range.from, to_day: range.to, format: 'csv' }, responseType: 'blob' });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = `${metric.replace(':', '-')}_${range.from}_${range.to}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={!!metric} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-4xl bg-[#0d0d0d] border-white/10 text-white" data-testid="finance-records-dialog">
                <DialogHeader>
                    <DialogTitle data-testid="finance-records-title">{title}</DialogTitle>
                    <DialogDescription className="text-[#A0AAB2] text-xs" data-testid="finance-records-definition">{data?.definition}</DialogDescription>
                </DialogHeader>
                {!data ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-8" /> : (
                    <>
                        <div className="flex items-center justify-between text-sm">
                            <span data-testid="finance-records-total">{data.count} records · {data.total_label || 'total'} <b className="font-mono">{fmt(data.total)}</b>{data.truncated && ' (first 500 shown; export has all)'}</span>
                            <button type="button" onClick={exportCsv} data-testid="finance-records-export-btn"
                                className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-xs hover:bg-white/5 transition-colors">
                                <Download className="w-3.5 h-3.5" /> Export CSV
                            </button>
                        </div>
                        <div className="max-h-[55vh] overflow-auto mt-2">
                            <table className="w-full text-xs" data-testid="finance-records-table">
                                <thead className="text-[#A0AAB2] sticky top-0 bg-[#0d0d0d]">
                                    <tr><th className="text-left p-1.5">When (CT)</th><th className="text-left p-1.5">Player</th><th className="text-left p-1.5">Record</th>
                                        <th className="text-left p-1.5">Method / game</th><th className="text-right p-1.5">{metric?.startsWith('game:') ? 'House effect' : 'Amount'}</th></tr>
                                </thead>
                                <tbody>
                                    {data.rows.map((r, i) => (
                                        <tr key={`${r.id}-${i}`} className="border-t border-white/5" data-testid={`finance-record-row-${i}`}>
                                            <td className="p-1.5 whitespace-nowrap">{fmtAt(r.at)}</td>
                                            <td className="p-1.5">{r.player}</td>
                                            <td className="p-1.5">{r.kind}<span className="text-[#666]"> · {r.type}</span></td>
                                            <td className="p-1.5">{r.method}</td>
                                            <td className="p-1.5 text-right font-mono">{fmt(r.house_effect ?? r.amount)}{r.reserved != null && <span className="text-[#666]"> (reserved {fmt(r.reserved)})</span>}</td>
                                        </tr>
                                    ))}
                                    {data.rows.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-[#666]">No records in this period</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
